import { useCallback, useEffect, useState } from 'react';

import {
  ActivityBuilderDrawerHeader,
  ActivityBuilderDrawerWrapper,
  ActivityBuilderDrawerContent,
} from 'approot/activity-builder/activity-builder-drawer';
import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import ComponentImageField from '../../shared/component-image-field/component-image-field';
import { FileDropperContextProvider } from 'lib/form/file-dropper/file-dropper.context';
import RiveEditUpload from '../rive-edit-upload';
import RiveModal from '../rive-modal';
import {
  SettingsGroup,
  SettingsHr,
  SettingsLabel,
} from 'approot/activity-builder/settings/settings.style';
import { useSimpleModalState } from 'approot/shared/modal/modal.hooks';
import { useBatchComponentUpdateContext } from '../../shared/batch-component-update.context';
import { ImageType } from 'global/constants';
import RiveSettingsTextLabels from './rive-settings-text-labels';
import { EditProps, RiveAnimationProperties } from '../rive.constants';
import RiveSettingsButtons from './rive-settings-buttons';
import Switch from 'approot/shared/switch/switch';

const RiveSettings = (props: EditProps) => {
  const { id, sectionId, type, properties } = props;

  const [newFileProps, setNewFileProps] = useState<{
    key: string;
    fileName: string;
    file: File;
  }>();

  const { closeDrawer } = useActivityBuilderContext();
  const { isVisible, openModal, closeModal } = useSimpleModalState();
  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const update = useCallback(
    (properties: RiveAnimationProperties) => {
      if (!properties.image) {
        return;
      }

      const { imageUrls, ...restProperties } = properties;
      const { original, ...restImage } = properties.image;

      updateComponentProperties({
        typename: 'ActivitiesRiveAnimationComponent',
        properties,
        input: {
          sectionId,
          componentId: id,
          componentData: {
            type,
            properties: {
              ...restProperties,
              image: {
                ...restImage,
                imageType: 'content' as ImageType,
              },
              rive: {
                key: properties.rive.key,
                fileName: properties.rive.fileName,
                sourceUrl: properties.sourceUrl,
                textLabels: properties.rive.textLabels,
                enableResetButton: properties.rive.enableResetButton,
                enableTextLabels: properties.rive.enableTextLabels,
                buttons: properties.rive.buttons,
                enableButtons: properties.rive.enableButtons,
                buttonsPosition: properties.rive.buttonsPosition,
              },
            },
          },
        },
      });
    },
    [id, sectionId, type, updateComponentProperties]
  );

  useEffect(() => {
    if (!properties.rive?.key) {
      setNewFileProps(undefined);
    }
  }, [properties.rive?.key]);

  return (
    <ActivityBuilderDrawerWrapper
      captureKeypresses={false}
      onCloseByEscape={closeDrawer}
    >
      {isVisible && (
        <RiveModal
          {...props}
          fileToUpload={newFileProps}
          onClose={closeModal}
        />
      )}
      <ActivityBuilderDrawerHeader
        title="Rive Animation"
        onClose={closeDrawer}
      />
      <ActivityBuilderDrawerContent>
        <SettingsGroup>
          <SettingsLabel>Rive File</SettingsLabel>
          {properties.rive?.key ? (
            <ComponentImageField image={properties.image} onEdit={openModal} />
          ) : (
            <FileDropperContextProvider>
              <RiveEditUpload
                id={props.id}
                onShowModal={newFileProps => {
                  setNewFileProps(newFileProps);
                  openModal();
                }}
              />
            </FileDropperContextProvider>
          )}
        </SettingsGroup>
        {!properties.rive?.key && properties.image && (
          <SettingsGroup>
            <SettingsLabel>Static Image for Print</SettingsLabel>
            <ComponentImageField
              image={{ ...properties.image, alt: properties.image.alt }}
              ariaLabel="Edit Static Image for Print"
              onEdit={openModal}
            />
          </SettingsGroup>
        )}
        <SettingsHr />
        <SettingsGroup>
          <SettingsLabel>Enable Reset Button</SettingsLabel>
          <Switch
            ariaLabel="Enable Reset Button"
            state={!!properties.rive?.enableResetButton}
            onClick={() =>
              update({
                ...properties,
                rive: {
                  ...properties.rive,
                  enableResetButton: !properties.rive?.enableResetButton,
                },
              })
            }
          />
        </SettingsGroup>

        <SettingsHr />

        <RiveSettingsButtons {...props} onUpdate={update} />

        <SettingsHr />

        <RiveSettingsTextLabels {...props} onUpdate={update} />
      </ActivityBuilderDrawerContent>
    </ActivityBuilderDrawerWrapper>
  );
};

export default RiveSettings;
